/**
 * Mini-player GLOBAL persistente (Fase 19.D+). Vive en `AppShell` (sobre las
 * rutas, junto al <audio> único del `ListenProvider`) y flota anclado al fondo
 * del frame para controlar la reproducción de "Escuchar" mientras navegás por el
 * resto de la app (home, programa, biblioteca, progreso, perfil, actividad…).
 *
 * Reutiliza el mismo `ListenMiniPlayer` de la conversación — misma UI, control
 * real (play/pausa) + expandir al player. Se OCULTA donde ya hay un control de
 * "Escuchar" propio, para no duplicar:
 *   - `/listen*`  → la biblioteca tiene su card "Seguí escuchando" + el player.
 *   - `/conversation` → monta su propio `ListenMiniPlayer` inline.
 *
 * `/home` estuvo en esa lista hasta el 2026-09-14 (su card de Escuchar hacía de
 * control), y era la única pantalla donde el reproductor cambiaba de LUGAR y de
 * COLOR: la fila de la card es el componente compartido de "continuá donde
 * quedaste", teñido con el violeta de la sección (ver `HeroCards`). Hoy el Home
 * muestra esta barra como cualquier otra pestaña y es la fila de la card la que
 * se corre mientras la sesión está viva (`useGlobalListenBarActive`), así el
 * reproductor vive SIEMPRE en el mismo lado con la misma identidad.
 *
 * En rutas con tab bar se posiciona por encima de él (`is-above-tabbar`); en las
 * demás, pegado al fondo (respetando el safe-area). Sólo móvil/tablet — el
 * escritorio tiene su propio layout (`.glb-wrap` es `display:none` ≥1024px, y
 * por eso la fila de la card NO se corre ahí).
 *
 * NO SE PLANTA ENCIMA DE UNA HOJA: LA ACOMPAÑA (2026-09-15, card #281).
 * Hasta esa fecha la barra vivía clavada abajo y una hoja abierta le quedaba
 * debajo, así que le tapaba el contenido — en la ficha de la Biblioteca se
 * comía justo la traducción de la palabra. Ahora, cuando hay una hoja abierta,
 * la barra se sube a apoyarse en su borde (`is-lifted`, `--glb-lift` = alto de
 * la hoja) y baja con ella al cerrarse. Es el MISMO elemento con una
 * transición: no se remonta, no cambia de forma y el viaje se ve.
 *
 * El alto de la hoja llega por `sheetLift`, que lo publica `useSheetDrag` —
 * el embudo por el que pasan todas las hojas de la app. Ninguna hoja cablea
 * nada, y la que se agregue mañana tampoco.
 *
 * Que la barra ENTRE entera arriba de la hoja no es gratis: las hojas reservan
 * esa franja (`--glb-room` en `app-shell.css`). Sin eso una hoja alta (los
 * Ajustes de la charla piden 92% de la pantalla) la dejaría cortada.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { useListen } from './ListenProvider.js';
import { useGlobalListenBarActive } from './ListenContext.js';
import { useOpenSheetHeight } from '../../hooks/sheetLift.js';
import { ListenMiniPlayer } from './ListenMiniPlayer.js';

/** Rutas que ya tienen un control de "Escuchar" propio → no flotar acá. */
const HIDDEN_PREFIXES = ['/listen', '/conversation'] as const;

/** La ruta donde la barra RELEVA a otro control y por eso el cambio se anima. */
const ANIMATED_PREFIX = '/home';

/**
 * Cuánto sigue montado el nodo después de apagarse, para que alcance a animar
 * la salida. Es la COTA SUPERIOR de `--glb-dur` (tokens.css): la salida en sí
 * termina a los 0.65 × 300 ms = 195 ms. Errar por largo sólo agrega unos ms de
 * nodo invisible; errar por corto corta el fade a la mitad, que se ve. Si
 * cambiás `--glb-dur`, este número tiene que quedar >= ese valor.
 */
const EXIT_MS = 300;

/** Rutas que renderizan el tab bar inferior → flotar por encima de él. */
const TABBAR_PREFIXES = ['/home', '/activity', '/library', '/progress', '/profile'] as const;

/**
 * La condición de sesión de la barra (la de RUTA es privada de este módulo)
 * vive en `ListenContext` y se re-exporta acá porque este es su consumidor
 * canónico y donde la busca todo el mundo. Ver el porqué de la mudanza ahí.
 */
export { useGlobalListenBarActive } from './ListenContext.js';

/**
 * Mantiene `true` hasta `ms` después de que `active` se apaga, para que el nodo
 * llegue a animar su salida antes de desmontarse. Sólo se usa donde la salida
 * se anima: en el resto de las rutas el nodo se va en el acto, porque una barra
 * quieta y visible 300 ms después de tocar la X sería peor que el corte.
 */
function useExitDelay(active: boolean, ms: number): boolean {
  const [present, setPresent] = useState(active);
  useEffect(() => {
    if (active) {
      setPresent(true);
      return undefined;
    }
    const t = setTimeout(() => setPresent(false), ms);
    return () => clearTimeout(t);
  }, [active, ms]);
  return present;
}

export function GlobalListenBar() {
  const L = useListen();
  const active = useGlobalListenBarActive();
  const lingering = useExitDelay(active, EXIT_MS);
  const navigate = useViewNavigate();
  const { pathname } = useLocation();
  /** Alto de la hoja abierta (0 = ninguna): a eso se sube la barra. */
  const sheetH = useOpenSheetHeight();

  const matches = (p: string): boolean => pathname === p || pathname.startsWith(`${p}/`);
  const animated = matches(ANIMATED_PREFIX);

  if (!(animated ? lingering : active)) return null;
  if (HIDDEN_PREFIXES.some(matches)) return null;

  const aboveTabbar = TABBAR_PREFIXES.some(matches);
  const lifted = sheetH > 0;
  const cls = [
    'glb-wrap',
    aboveTabbar ? 'is-above-tabbar' : '',
    lifted ? 'is-lifted' : '',
    animated ? 'is-animated' : '',
    animated && !active ? 'is-leaving' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cls}
      style={lifted ? ({ '--glb-lift': `${sheetH}px` } as CSSProperties) : undefined}
    >
      <ListenMiniPlayer
        onExpand={() => void navigate('/listen/player')}
        onClose={() => L.close()}
      />
    </div>
  );
}
